import { cn } from '../../lib/utils';
import { useId } from 'react';
import { textVariants } from './variants';
import type { CircularProgressProps } from './types';
import useI18n from '../../hooks/useI18n';
import { useLocaleFormat } from '../../hooks/useLocaleFormat';

const sizeMap = {
  big: { size: 280, stroke: 4, thickness: 34 },
  default: { size: 156, stroke: 3, thickness: 21 },
  medium: { size: 104, stroke: 3, thickness: 14 },
  small: { size: 32, stroke: 2, thickness: 4 },
};

const getColorClass = (value: number) => {
  if (value <= 25) return 'eye:stroke-grey-100';
  if (value <= 50) return 'eye:stroke-blue-50';
  if (value <= 85) return 'eye:stroke-blue-100';
  return 'eye:stroke-blue-150';
};

function CircularProgress({
  value,
  previousValue,
  max = 100,
  size,
  ariaLabel,
  ariaLabelPreviousValue,
  textClassName,
  progressColorClassName,
  valueLabel,
  disabled = false,
}: CircularProgressProps) {
  const { t } = useI18n();
  const { formatNumber } = useLocaleFormat();
  const id = useId();

  const { size: viewBoxSize, stroke, thickness } = sizeMap[size || 'default'];
  const viewBoxSizeHalf = viewBoxSize / 2;
  const radius = (viewBoxSize - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const progressOffset = circumference * (1 - (value || 0) / max);

  const isLoading = value === undefined;
  const isValidNumber =
    typeof previousValue === 'number' && !Number.isNaN(previousValue);

  const label = ariaLabel || t('currentValue');

  return (
    <div
      className='eye:relative eye:flex eye:items-center eye:justify-center'
      {...(isLoading && { role: 'progressbar', 'aria-label': label })}
      style={
        {
          '--progress-offset': progressOffset,
          '--circumference': circumference,
          '--circumference99': circumference * -0.99,
          '--circumference50': circumference * 0.5,
          '--circumference20': circumference * -0.2,
        } as React.CSSProperties
      }
    >
      {!isLoading && (
        <div
          className={cn(
            textVariants({ size }),
            disabled && 'eye:text-grey-disabled',
            textClassName,
          )}
        >
          <div>
            <span className='eye:sr-only'>{label} </span>
            {valueLabel ?? formatNumber(value + '%')}
          </div>
          {isValidNumber && (
            <div className='eye:body-m eye:line-through'>
              <span className='eye:sr-only'>
                {ariaLabelPreviousValue || t('previousValue')}{' '}
              </span>
              {formatNumber(previousValue + '%')}
            </div>
          )}
        </div>
      )}
      <svg
        width={viewBoxSize}
        height={viewBoxSize}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className={isLoading ? 'eye:animate-spin' : ''}
        aria-hidden='true'
      >
        {/* Outer Mask to create additional cutout */}
        <mask id={`${id}-donut-mask`}>
          <rect width='100%' height='100%' fill='black' />
          <circle
            cx={viewBoxSizeHalf}
            cy={viewBoxSizeHalf}
            r={radius + thickness / 2}
            fill='white'
          />
          <circle
            cx={viewBoxSizeHalf}
            cy={viewBoxSizeHalf}
            r={radius - thickness / 2}
            fill='black'
          />
        </mask>
        <BackgroundCircle
          viewBoxSizeHalf={viewBoxSizeHalf}
          radius={radius}
          thickness={thickness}
          maskId={`${id}-donut-mask`}
        />
        <ProgressCircle
          viewBoxSizeHalf={viewBoxSizeHalf}
          radius={radius}
          thickness={thickness}
          stroke={stroke}
          circumference={circumference}
          colorClass={
            disabled
              ? 'eye:stroke-grey-100'
              : progressColorClassName || getColorClass(value || 0)
          }
          isLoading={isLoading}
          maskId={`${id}-donut-mask`}
        />
      </svg>
    </div>
  );
}

const BackgroundCircle = ({
  viewBoxSizeHalf,
  radius,
  thickness,
  maskId,
}: {
  viewBoxSizeHalf: number;
  radius: number;
  thickness: number;
  maskId: string;
}) => (
  <circle
    cx={viewBoxSizeHalf}
    cy={viewBoxSizeHalf}
    r={radius}
    strokeWidth={thickness}
    className='eye:stroke-grey-20'
    fill='transparent'
    mask={`url(#${maskId})`}
  />
);

const ProgressCircle = ({
  viewBoxSizeHalf,
  radius,
  thickness,
  stroke,
  circumference,
  colorClass,
  isLoading,
  maskId,
}: {
  viewBoxSizeHalf: number;
  radius: number;
  thickness: number;
  stroke: number;
  circumference: number;
  colorClass: string;
  isLoading: boolean;
  maskId: string;
}) => (
  <>
    {/* White Outline for better visibility */}
    <circle
      cx={viewBoxSizeHalf}
      cy={viewBoxSizeHalf}
      r={radius}
      strokeWidth={thickness + stroke * 2}
      strokeLinecap='round'
      className='eye:animate-progress eye:stroke-white'
      fill='transparent'
      strokeDasharray={circumference}
      strokeDashoffset={circumference}
      transform={`rotate(-90 ${viewBoxSizeHalf} ${viewBoxSizeHalf})`}
      mask={`url(#${maskId})`}
    />
    {/* Progress Fill */}
    <circle
      cx={viewBoxSizeHalf}
      cy={viewBoxSizeHalf}
      r={radius}
      strokeWidth={thickness}
      strokeLinecap='round'
      className={
        isLoading
          ? 'eye:animate-dash eye:stroke-blue-50'
          : `eye:animate-progress ${colorClass}`
      }
      fill='transparent'
      strokeDasharray={circumference}
      strokeDashoffset={circumference}
      transform={`rotate(-90 ${viewBoxSizeHalf} ${viewBoxSizeHalf})`}
      mask={`url(#${maskId})`}
    />
  </>
);

export { CircularProgress };
